import fetch from 'axios';
import queryString from 'querystring';
import url from 'url';

import cookie from '@snapask/common-components/dist/utils/cookie';
import {
  TOKEN,
  DEVICE_ID,
} from '@snapask/common-components/dist/constants/cookieNames';

import config from '@blueberry/config';

import { getLanguage, getRegion } from '@blueberry/redux/locale';
import { getRegion as getUserRegion } from '@blueberry/redux/profile';
import { getDeviceId, getHeaders, getToken } from '@blueberry/redux/auth';

import { getStore } from '@blueberry/store/configureStore';
import { ERROR } from '@blueberry/constants/http';

export const getOSType = type => {
  if (type.indexOf('Win') !== -1) return 'Windows';
  if (type.indexOf('Android') !== -1) return 'Android';
  if (type.indexOf('Mac') !== -1 && type.indexOf('Mobile') !== -1) return 'iOS';
  if (type.indexOf('Mac') !== -1) return 'MacOS';
  if (type.indexOf('X11') !== -1) return 'UNIX';
  if (type.indexOf('Linux') !== -1) return 'Linux';

  return 'Unknown OS';
};

export const buildApiUrl = (apiPath, queries = {}, lang) => {
  const apiUrl = url.parse(`${config.BACKEND_API_ENDPOINT}/${apiPath}`);

  // get pre-existed query
  let queryObject = (apiUrl.query && queryString.parse(apiUrl.query)) || {};

  // merge queries
  // make sure lang in format: en-HK (regions should be CAPITAL)
  const capitalizedRegionLang = lang.replace(
    /(\w+)-(\w+)/,
    (match, p1, p2) => `${p1}-${p2.toUpperCase()}`
  );
  queryObject = Object.assign(queryObject, queries, {
    lang: capitalizedRegionLang,
  });

  // if query object is not empty, assign query
  if (Object.keys(queryObject).length) {
    apiUrl.search = queryString.stringify(queryObject);
  }

  return apiUrl.format();
};

/**
 * @TODOs
 *  - redirect to login page when token expired.
 *  - implement general http error actions. Dispatch error action.
 *
 * @param {String} path
 * @param {String} method
 * @param {Object} headers Header data that is about to send with this request.
 * @param {Object} reqHeader Header data coming from the web to generate os type.
 * @param {Object} options
 */
export const fetchApi = (
  path,
  method = 'GET',
  { headers = {}, params, ...options } = {}
) => {
  // Getting the language directly from
  // redux store. This is needed for backend to provide
  // error message in proper i18n language.
  const reduxStore = getStore().getState();
  const lang = getLanguage(reduxStore);
  const deviceIDInStore = getDeviceId(reduxStore);
  const headerInStore = getHeaders(reduxStore);
  const tokenInStore = getToken(reduxStore);
  const regionInStore = getRegion(reduxStore);
  const userRegionInStore = getUserRegion(reduxStore);

  // get api url
  const apiUrl = buildApiUrl(path, params, lang);

  const snapCookie = new cookie(config.COOKIE_DOMAIN);
  // only read cookie on the client side.
  const token = __CLIENT__ ? snapCookie.readCookie(TOKEN) : tokenInStore;
  const deviceId = __CLIENT__
    ? snapCookie.readCookie(DEVICE_ID)
    : deviceIDInStore;
  const os = getOSType(
    __CLIENT__ ? navigator.userAgent : headerInStore.userAgent
  );

  // assign headers
  const fetchHeaders = {
    ...headers,
    'Content-Type': 'application/json',
    'Snapask-User-Agent': JSON.stringify({
      device_id: deviceId,
      os,
      platform: 'web',
    }),
    'Region-Name': userRegionInStore || regionInStore,
  };

  if (token) {
    fetchHeaders['Snapask-Authorization'] = `Bearer ${token}`;
  }

  return fetch(apiUrl, {
    method,
    headers: fetchHeaders,
    ...options,
  })
    .then(res => res.data.data)
    .catch(err => {
      const {
        response: {
          status: statusCode,
          data: { status, error_code, message, response },
        },
      } = err;

      if (status === ERROR) {
        const error = {
          statusCode,
          error_code,
          // Rails and Golang response format are different
          message: message || response,
        };
        throw error;
      }
    });
};

/**
 * Short handed method for POST
 */
export const postApi = (path, { headers, ...options } = {}) =>
  fetchApi(path, 'POST', {
    headers,
    ...options,
  });

/**
 * Short handed method for GET
 */
export const getApi = (path, { headers, ...options } = {}) =>
  fetchApi(path, 'GET', {
    headers,
    ...options,
  });

/**
 * Short handed method for DELETE
 */
export const deleteApi = (path, { headers, ...options } = {}) =>
  fetchApi(path, 'DELETE', {
    headers,
    ...options,
  });



// WEBPACK FOOTER //
// ./app/services/utils.js